import * as React from 'react';
import * as ReactSelectModule from 'react-select';
import { Solution } from 'src/models/Solution';
import { IAuthenticationResult } from '../authentication/Authentication';
import { APIEndpoints, POPULAR_ITEMS_COUNT } from '../components/Globals/Globals';
import { IOptionInterface } from '../components/MultiSelectFilter/IOptionInterface';
import { OfferingTypeFilter } from '../models/OfferingTypeFilter';
import { SolutionFile } from '../models/SolutionFile';
import { arrayContainsAnotherArray, decodeHTML } from '../tools/CommonFunctions';

export interface IMultiselectItem {
  value: string;
  label: string;
}

export interface IAppState {
  selectedIndustryOptions: ReactSelectModule.Option[];
  selectedBusinessLineOptions: ReactSelectModule.Option[];
  selectedProductOptions: ReactSelectModule.Option[];
  selectedSolution: Solution | null;
  offeringTypes: IOptionInterface[];
  valueToCustomerTypes: IOptionInterface[];
  apiUrl: string;
  solutions: Solution[];
  filteredSolutions: Solution[];
  latestSolutions: Solution[];
  mostViewedSolutions: IMostViewedSolutions[];
  mostPopularSolutions: IMostViewedSolutions[];
  mostPopularMaterials: SolutionFile[];
  latestMaterials: SolutionFile[];
  solutionsLoaded: boolean;
  solutionLoadError: boolean;
  industryOptions: ReactSelectModule.Option[];
  businessLineOptions: ReactSelectModule.Option[];
  productOptions: ReactSelectModule.Option[];
  textSearch: string;
  authenticationToken: string;
}

export interface IFilterActions {
  generateFilters(solutions: Solution[]): void;
  addIndustries(items: ReactSelectModule.Option[]): void;
  addBusinesses(items: ReactSelectModule.Option[]): void;
  addProducts(items: ReactSelectModule.Option[]): void;
  filterSolutions(): void;
  resetFilters(): Promise<{}>;
  clearTextSearch(): Promise<{}>;
  setSelectedSolution(id: string): Solution | null;
  filterSolutionsByText(text: string): void;
}

export interface IOfferingTypeActions {
  showValueToCustomer(offeringTypes: IOptionInterface[]): boolean | undefined;
}

export interface IApiActions {
  getSolutions(apiUrl: string): Solution[] | null;
  getSolutionFromSolutionsList(id: string): Solution | undefined | null;
  fetchAllSolutionsFromApi(): Promise<Solution[]>;
  getMaterialFromApi(id: string, fileType: string, fileTitle?: string): Promise<string>;
  waitForSolutionsLoaded(): Promise<{}>;
  parseSolutionImageHtml(imageHtml: string): Promise<string>;
  fetchFileThumbnailInBase64(fileDocumentId: string): Promise<string>;
  getPreview(id: string, fileType: string): Promise<string>;
  getFilterOptions(): Promise<OfferingTypeFilter>;
  incrementSolutionView(id: string): void;
  getSolutionViews(id: string): Promise<string>;
  getMostViewedAndPopularSolutions(count: number): Promise<IMostViewedSolutionsResponse>;
  getPopularMaterials(count: number): SolutionFile[];
}

export interface IAppContextProps {
  actions: IFilterActions;
  state: IAppState;
  offeringTypeActions: IOfferingTypeActions;
  apiActions: IApiActions;
}

export interface IAppContextComponentProps {
  authenticationResult: IAuthenticationResult;
}

export interface IMostViewedSolutions {
  id: string;
  views: number;
  solution: Solution | undefined;
}

export interface IMostViewedSolutionsResponse {
  mostViewed: IMostViewedSolutions[];
  mostPopular: IMostViewedSolutions[];
}

const unimplementedFunction = () => {
  throw new Error('Not implemented!');
};

// Constants
const apiUrl = '/api/';
const LATEST_SOLUTIONS_SHOWN = 5;
const LATEST_MATERIALS_SHOWN = 5;
const POPULAR_MATERIALS_SHOWN = 5;

const Context = React.createContext<IAppContextProps>({
  actions: {
    generateFilters: unimplementedFunction,
    addIndustries: unimplementedFunction,
    addBusinesses: unimplementedFunction,
    addProducts: unimplementedFunction,
    filterSolutions: unimplementedFunction,
    resetFilters: unimplementedFunction,
    clearTextSearch: unimplementedFunction,
    setSelectedSolution: unimplementedFunction,
    filterSolutionsByText: unimplementedFunction
  },
  state: {
    offeringTypes: [],
    valueToCustomerTypes: [],
    apiUrl: '',
    solutions: [],
    filteredSolutions: [],
    latestSolutions: [],
    latestMaterials: [],
    mostViewedSolutions: [],
    mostPopularSolutions: [],
    mostPopularMaterials: [],
    selectedSolution: null,
    solutionsLoaded: false,
    solutionLoadError: false,
    industryOptions: [],
    selectedIndustryOptions: [],
    businessLineOptions: [],
    selectedBusinessLineOptions: [],
    productOptions: [],
    selectedProductOptions: [],
    textSearch: '',
    authenticationToken: ''
  },

  offeringTypeActions: {
    showValueToCustomer: unimplementedFunction
  },

  apiActions: {
    getSolutions: unimplementedFunction,
    getSolutionFromSolutionsList: unimplementedFunction,
    fetchAllSolutionsFromApi: unimplementedFunction,
    getMaterialFromApi: unimplementedFunction,
    waitForSolutionsLoaded: unimplementedFunction,
    parseSolutionImageHtml: unimplementedFunction,
    fetchFileThumbnailInBase64: unimplementedFunction,
    getPreview: unimplementedFunction,
    getFilterOptions: unimplementedFunction,
    incrementSolutionView: unimplementedFunction,
    getSolutionViews: unimplementedFunction,
    getMostViewedAndPopularSolutions: unimplementedFunction,
    getPopularMaterials: unimplementedFunction
  }
});

export class AppProvider extends React.Component<IAppContextComponentProps, IAppState> {
  public offeringTypes = [];

  public offeringTypeActions = {
    showValueToCustomer: (offeringTypes: IOptionInterface[]): boolean | undefined => {
      const option = offeringTypes.find((offering: IOptionInterface) => {
        return offering.label === 'Upgrade solutions';
      });
      return option && option.selected === true;
    }
  };
  /** Functions to get solution data from the API. */
  public apiActions = {
    /** Returns all soultuons. Returns null if solutions are not fetched first. */
    getSolutions: () => {
      return this.state.solutions ? this.state.solutions : null;
    },
    /** Fetches all solutions from the API */
    fetchAllSolutionsFromApi: () => {
      const apiEndpoints = APIEndpoints;
      const authenticationContext = this.props.authenticationResult.authenticationContext;
      return new Promise<string | null>((resolve, reject) => {
        if (authenticationContext) {
          authenticationContext.acquireToken(
            authenticationContext.config.clientId,
            (err: string, token: string) => {
              if (err) {
                /* We get to this block when Adal wants interactive login or cannot get token
                   latter might happen, if user has signed in using other account*/
                authenticationContext.login(); // We just force login for now. This isn't sort of peculiar.
                reject(err);
              }
              this.setState({ authenticationToken: token });
              resolve(token);
            }
          );
        }
      })
        .then((token: string) =>
          fetch(this.state.apiUrl + apiEndpoints.allSolutions, {
            headers: new Headers({
              X_Authorization: 'Bearer ' + token
            })
          })
        )
        .then(response => response.json())
        .then((solutionMessage: any) => {
          return solutionMessage[0].payload as Solution[];
        });
    },
    /** Gets a solution with the specified ID from the solutions list. */
    getSolutionFromSolutionsList: (id: string) => {
      if (this.state.solutions) {
        return this.state.solutions.find(solution => solution.id === id);
      } else {
        return null;
      }
    },
    /** Gets a material from the API. Returns url for the material. */
    getMaterialFromApi: (id: string, fileType: string, fileTitle?: string) => {
      let url = `${this.state.apiUrl +
        APIEndpoints.materials}/${id}?fileType=${fileType.toLowerCase()}`;
      if (fileTitle) {
        url += `&fileTitle=${fileTitle}`;
      }
      return fetch(url, {
        headers: new Headers({
          X_Authorization: 'Bearer ' + this.state.authenticationToken
        })
      }).then(res => {
        return res.json();
      });
    },
    /** Gets a preview from the API. Returns url for the preview. */
    getPreview: (id: string) => {
      return fetch(`${this.state.apiUrl + APIEndpoints.previews}/${id}`, {
        headers: new Headers({
          X_Authorization: 'Bearer ' + this.state.authenticationToken
        })
      }).then(res => {
        return res.json();
      });
    },
    /** Returns a promise that is resolved either when solutions are loaded or an error occurs in the loading process. */
    waitForSolutionsLoaded: () => {
      return new Promise((resolve, reject) => {
        const waitForLoad = () => {
          if (
            (this.state.solutionsLoaded && this.state.solutions) ||
            this.state.solutionLoadError
          ) {
            resolve();
          } else {
            setTimeout(waitForLoad, 100);
          }
        };
        waitForLoad();
      });
    },
    /**
     * Parses html img tag that comes from salesforce. img src URLs are only available from the API, so we can't use their url:s directly.
     * All the src tags are converted to base64 in the API. This functon returns the img tag with sources converted to base64.
     */
    parseSolutionImageHtml: (imageHtml: string): Promise<string> => {
      // Regex that gets everything between src=" and ".
      const srcRegex = RegExp('src="(.*?)"', 'g');
      const imagePromises: Array<Promise<string>> = [];

      // Find all src values
      const regexResult = imageHtml.match(srcRegex);

      if (!regexResult) {
        return Promise.resolve(imageHtml);
      }

      // Regex results come in the form of 'src="..."', from which we get the src value by slicing.
      // We will also check for any possible empty src's and filter them out.
      const imageSrcs = regexResult
        .map(result => result.slice(5, -1))
        .filter(result => result.length > 0);

      // tslint:disable-next-line:no-conditional-assignment
      for (const imgSrc of imageSrcs) {
        const decodedUrl = decodeHTML(imgSrc);
        if (decodedUrl) {
          const { searchParams } = new URL(decodedUrl);
          let solutionId;
          let imageId;
          if (searchParams) {
            // Use native browser version
            solutionId = searchParams.get('eid');
            imageId = searchParams.get('refid');
          } else {
            // Use custom parser when searchParams is not supported
            const { eid, refid } = decodedUrl
              .slice(decodedUrl.indexOf('?') + 1)
              .split('&')
              .reduce((acc: any, hash) => {
                const split = hash.indexOf('=');
                const key = hash.slice(0, split);
                const val = hash.slice(split + 1);
                return { ...acc, [key]: decodeURIComponent(val) };
              }, {});
            solutionId = eid;
            imageId = refid;
          }
          imagePromises.push(
            fetch(
              `${this.state.apiUrl}${APIEndpoints.allSolutions}/${solutionId}/image/${imageId}`,
              {
                headers: new Headers({
                  X_Authorization: 'Bearer ' + this.state.authenticationToken
                })
              }
            ).then(res => {
              return res.text();
            })
          );
        }
      }

      if (imagePromises.length > 0) {
        // Wait for all imaages from the api
        return Promise.all(imagePromises).then(values => {
          imageSrcs.forEach((src, i) => {
            // Replace the image src values with their base64 counterparts
            let base64Image = values[i];
            // Remove "- characters from start and end of the response.
            base64Image = base64Image.slice(1, -1);
            imageHtml = imageHtml.replace(src, `data:image/jpg;base64, ${base64Image}`);
          });
          return Promise.resolve(imageHtml);
        });
      } else {
        return Promise.resolve(imageHtml);
      }
    },
    /** Fetches thumbnail for a material and retuns it as base64 string */
    fetchFileThumbnailInBase64: (fileDocumentId: string) => {
      return fetch(`${this.state.apiUrl}${APIEndpoints.thumbnails}/${fileDocumentId}`, {
        headers: new Headers({
          X_Authorization: 'Bearer ' + this.state.authenticationToken
        })
      }).then(res => {
        return res.text();
      });
    },
    /** Gets filtering options from the API. For example Offering Type and Value to customer filters */
    getFilterOptions: () => {
      return fetch(`${this.state.apiUrl}${APIEndpoints.filterOptions}/`, {
        headers: new Headers({
          X_Authorization: 'Bearer ' + this.state.authenticationToken
        })
      }).then(res => {
        return res.json() as Promise<OfferingTypeFilter>;
      });
    },
    getMostViewedAndPopularSolutionsFromApi: (count = 5) => {
      return fetch(`${this.state.apiUrl}${APIEndpoints.mostViewed}?dateRange=90&count=${count}`, {
        headers: new Headers({
          X_Authorization: 'Bearer ' + this.state.authenticationToken
        })
      }).then(res => {
        return res.json() as Promise<IMostViewedSolutionsResponse>;
      });
    },
    incrementSolutionView: (id: string) => {
      fetch(`${this.state.apiUrl}${APIEndpoints.allSolutions}/${id}/addView`, {
        headers: new Headers({
          X_Authorization: 'Bearer ' + this.state.authenticationToken
        })
      });
    },
    getSolutionViews: async (id: string, dateRange?: number) => {
      let url = `${this.state.apiUrl}${APIEndpoints.allSolutions}/${id}/views`;
      url += dateRange ? '?dateRange=' + dateRange.toString() : '';
      return fetch(url, {
        headers: new Headers({
          X_Authorization: 'Bearer ' + this.state.authenticationToken
        })
      }).then(response => response.text());
    },
    /** Gets most viewed and most popular solutions */
    getMostViewedAndPopularSolutions: async (
      count: number = POPULAR_ITEMS_COUNT
    ): Promise<IMostViewedSolutionsResponse> => {
      const views = await this.apiActions.getMostViewedAndPopularSolutionsFromApi(count);
      if (views && views.mostPopular && views.mostViewed) {
        views.mostPopular.forEach(viewModel => {
          viewModel.solution = this.state.solutions.find(solution => solution.id === viewModel.id);
        });
        views.mostViewed.forEach(viewModel => {
          viewModel.solution = this.state.solutions.find(solution => solution.id === viewModel.id);
        });
      }
      return views;
    },

    getPopularMaterials: (count: number = POPULAR_MATERIALS_SHOWN): SolutionFile[] => {
      let solutionFiles: SolutionFile[] = [];
      for (const sol of this.state.solutions) {
        if (sol.files && sol.files.length > 0) {
          sol.files.map(file => (file.relatedSolutionId = sol.id));
          solutionFiles = solutionFiles.concat(sol.files);
        }
      }
      return solutionFiles
        .sort((a, b) => {
          const aViews = a.contentVersionDownloadedCount || 0;
          const bViews = b.contentVersionDownloadedCount || 0;
          return aViews === bViews ? 0 : aViews < bViews ? 1 : -1;
        })
        .slice(0, count);
    }
  };

  public actions = {
    // Generates options based on the values on the solutions. These options are displayed in MultiSelectInput.
    generateFilters: (solutions: Solution[]) => {
      const businessLineOptions: ReactSelectModule.Option[] = [];
      const industryOptions: ReactSelectModule.Option[] = [];
      const productOptions: ReactSelectModule.Option[] = [];

      solutions.forEach(solution => {
        solution.businessLine.forEach(businessLine => {
          if (
            businessLineOptions.findIndex(
              businessLineOption => businessLineOption.value === businessLine
            ) === -1
          ) {
            businessLineOptions.push({
              value: businessLine,
              label: businessLine
            });
          }
        });

        solution.industry.forEach(industry => {
          if (
            industryOptions.findIndex(industryOption => industryOption.value === industry) === -1
          ) {
            industryOptions.push({
              value: industry,
              label: industry
            });
          }
        });

        solution.applicableProducts.forEach(product => {
          if (productOptions.findIndex(productOption => productOption.value === product) === -1) {
            productOptions.push({
              value: product,
              label: product
            });
          }
        });
      });
      this.setState({ industryOptions, businessLineOptions, productOptions });
    },

    addIndustries: (items: ReactSelectModule.Option[]) => {
      this.setState(
        {
          selectedIndustryOptions: items
        },
        this.actions.filterSolutions
      );
    },

    addBusinesses: (items: ReactSelectModule.Option[]) => {
      this.setState(
        {
          selectedBusinessLineOptions: items
        },
        this.actions.filterSolutions
      );
    },

    addProducts: (items: ReactSelectModule.Option[]) => {
      this.setState(
        {
          selectedProductOptions: items
        },
        this.actions.filterSolutions
      );
    },
    filterSolutionsByText: (text: string) => {
      this.setState({ textSearch: text }, this.actions.filterSolutions);
    },
    /** Resets all filters except text search */
    resetFilters: () => {
      return new Promise(resolve => {
        this.setState(
          {
            selectedBusinessLineOptions: [],
            selectedIndustryOptions: [],
            selectedProductOptions: [],
            offeringTypes: this.state.offeringTypes.map(selection => {
              return { ...selection, selected: false };
            })
          },
          () => resolve()
        );
      });
    },

    clearTextSearch: () => {
      return new Promise(resolve => {
        this.setState({ textSearch: '' }, () => resolve());
      });
    },

    /** Filters solutions based on selected filters. Changes 'filteredSolutions' state. */
    filterSolutions: () => {
      let filteredSolutions: Solution[] = this.state.solutions;

      // Business Line selection (MultiSelectInput)
      if (this.state.selectedBusinessLineOptions.length !== 0) {
        filteredSolutions = filteredSolutions.filter((solution: Solution) => {
          return arrayContainsAnotherArray(
            this.state.selectedBusinessLineOptions.map(option => option.value),
            solution.businessLine
          );
        });
      }

      // Industry selection (MultiSelectInput)
      if (this.state.selectedIndustryOptions.length !== 0) {
        filteredSolutions = filteredSolutions.filter((solution: Solution) => {
          return arrayContainsAnotherArray(
            this.state.selectedIndustryOptions.map(option => option.value),
            solution.industry
          );
        });
      }

      // Product selection (MultiSelectInput)
      if (this.state.selectedProductOptions.length !== 0) {
        filteredSolutions = filteredSolutions.filter((solution: Solution) => {
          return arrayContainsAnotherArray(
            this.state.selectedProductOptions.map(option => option.value),
            solution.applicableProducts
          );
        });
      }

      // Offering type selection (MultiSelectFilter)
      const selectedOfferingTypes = this.state.offeringTypes.filter(
        selection => selection.selected
      );
      if (selectedOfferingTypes.length > 0) {
        filteredSolutions = filteredSolutions.filter((solution: Solution) => {
          if (solution.offeringType) {
            return (
              selectedOfferingTypes.findIndex(
                selection => selection.label === solution.offeringType
              ) !== -1
            );
          } else {
            return false;
          }
        });
      }
      const selectedValueToCustomer = this.state.valueToCustomerTypes
        .filter(selection => selection.selected)
        .map(selectedSelection => selectedSelection.label);

      // Value to customer (only checking if Upgrade Solutions is selected from Offering Types.)
      if (
        selectedOfferingTypes &&
        selectedOfferingTypes.findIndex(
          selection => selection.label.toLowerCase() === 'upgrade solutions'
        ) !== -1 &&
        selectedValueToCustomer.length > 0
      ) {
        filteredSolutions = filteredSolutions.filter((solution: Solution) => {
          if (solution.valueToCustomer) {
            return solution.valueToCustomer.some(val => selectedValueToCustomer.indexOf(val) >= 0);
          } else {
            return false;
          }
        });
      }

      if (this.state.textSearch) {
        filteredSolutions = filteredSolutions.filter(
          solution =>
            solution.solutionName.toLowerCase().indexOf(this.state.textSearch.toLowerCase()) !== -1
        );
      }

      this.setState({ filteredSolutions });
    },
    /** Sets the currently displayed Solution in the Offering page. */
    setSelectedSolution: (id: string) => {
      const selectedSolution = this.state.solutions.find(solution => solution.id === id);
      if (selectedSolution) {
        this.setState({ selectedSolution });
        return selectedSolution;
      } else {
        return null;
      }
    }
  };

  constructor(params: any) {
    super(params);

    this.state = {
      selectedIndustryOptions: [],
      selectedBusinessLineOptions: [],
      selectedProductOptions: [],
      offeringTypes: [],
      valueToCustomerTypes: [],
      apiUrl,
      solutions: [],
      filteredSolutions: [],
      latestSolutions: [],
      latestMaterials: [],
      mostPopularSolutions: [],
      mostViewedSolutions: [],
      mostPopularMaterials: [],
      selectedSolution: null,
      solutionsLoaded: false,
      solutionLoadError: false,
      businessLineOptions: [],
      industryOptions: [],
      productOptions: [],
      textSearch: '',
      authenticationToken: ''
    };
  }

  /** Sorts solutions by releaseDate in descending order, and returns 4 latest. */
  public getLatestSolutions(): Solution[] {
    return this.state.solutions
      .sort((a, b) => {
        const aDate = new Date(a.releaseDate ? a.releaseDate : 0).getTime();
        const bDate = new Date(b.releaseDate ? b.releaseDate : 0).getTime();
        return aDate === bDate ? 0 : aDate < bDate ? 1 : -1;
      })
      .slice(0, LATEST_SOLUTIONS_SHOWN);
  }

  /** Sorts solution materials by releaseDate in descending order, and returns 4 latest. */
  public getLatestMaterials(): SolutionFile[] {
    let solutionFiles: SolutionFile[] = [];
    for (const sol of this.state.solutions) {
      if (sol.files && sol.files.length > 0) {
        sol.files.map(file => (file.relatedSolutionId = sol.id));
        solutionFiles = solutionFiles.concat(sol.files);
      }
    }
    return solutionFiles
      .sort((a, b) => {
        const aDate = new Date(a.lastModifiedDate).getTime();
        const bDate = new Date(b.lastModifiedDate).getTime();
        return aDate === bDate ? 0 : aDate < bDate ? 1 : -1;
      })
      .slice(0, LATEST_MATERIALS_SHOWN);
  }

  public async processFilterOptions() {
    // Fetch filter options for offering type
    this.apiActions.getFilterOptions().then(filters => {
      this.setState({
        offeringTypes: filters.controllingFieldPickLists.map((field, i) => {
          return {
            id: `${i}_${field.picklistValue}`,
            label: field.picklistValue,
            selected: false,
            helpText: field.helpText
          };
        }),
        valueToCustomerTypes: filters.dependentFieldPickLists.map((field, i) => {
          return {
            id: `${i}_${field.picklistValue}`,
            label: field.picklistValue,
            selected: false,
            helpText: field.helpText
          };
        })
      });
    });
  }

  public componentDidMount() {
    // Fetch all solutions from the api when page is loaded.
    this.apiActions.fetchAllSolutionsFromApi().then(
      solutions => {
        this.processFilterOptions();
        if (solutions) {
          this.setState(
            {
              solutions,
              filteredSolutions: solutions,
              solutionsLoaded: true
            },
            // Get the most viewed and popular solutions from API
            () => {
              this.setState({
                latestSolutions: this.getLatestSolutions(),
                latestMaterials: this.getLatestMaterials(),
                mostPopularMaterials: this.apiActions.getPopularMaterials()
              });
              this.apiActions.getMostViewedAndPopularSolutions().then(mostViewed =>
                this.setState({
                  mostPopularSolutions: mostViewed.mostPopular || [],
                  mostViewedSolutions: mostViewed.mostViewed || []
                })
              );
              this.actions.generateFilters(solutions);
            }
          );
        } else {
          this.setState({ solutionLoadError: true });
        }
      },
      error => {
        this.setState({ solutionLoadError: true });
      }
    );
  }

  public render() {
    return (
      <Context.Provider
        value={{
          actions: this.actions,
          state: this.state,
          offeringTypeActions: this.offeringTypeActions,
          apiActions: this.apiActions
        }}
      >
        {this.props.children}
      </Context.Provider>
    );
  }
}

export const AppConsumer = Context.Consumer;



// WEBPACK FOOTER //
// ./src/contexts/Context.tsx